import { ExpenseItem, SendEgresoEmailRequest } from '../models';

export interface EgresoEmailDetailRowMapperInput {
    proveedor?: string;
    file?: string;
    codigo?: string;
    codigoReserva?: string;
    monto?: number;
    moneda?: string;
    usuario?: string;
    observaciones?: string;
}

export interface BuildEgresoEmailRequestInput {
    toEmails: string[];
    ccEmails?: string[];
    subject: string;
    selectedProofFileName?: string;
    selectedProofRemoteId?: string;
    mainMessage: string;
    egresoDetails: EgresoEmailDetailRowMapperInput[];
}

/**
 * @description Mapea los detalles del egreso a un formato de lista de gastos (ExpenseItem) adecuado para su inclusión en la solicitud de envío de correo electrónico. 
 * - Normaliza los campos opcionales y asegura que los datos estén en el formato correcto para su uso en la plantilla de correo.
 * @param rows Detalles del egreso en formato de entrada, que pueden contener campos opcionales o con formato inconsistente.
 * @returns Lista de gastos (ExpenseItem) con campos normalizados y formateados adecuadamente para su inclusión en la solicitud de envío de correo electrónico.
 */
export function mapEgresoDetailsToExpenseItems(
    rows: EgresoEmailDetailRowMapperInput[],
): ExpenseItem[] {
    return rows.map((row) => ({
        supplier: String(row.proveedor ?? '').trim(),
        file: String(row.file ?? '').trim(),
        code: String(row.codigo ?? '').trim(),
        bookingCode: normalizeOptional(row.codigoReserva),
        amount: Number(row.monto ?? 0),
        currency: String(row.moneda ?? '').trim(),
        userEmail: String(row.usuario ?? '').trim().toLowerCase(),
        notes: normalizeOptional(row.observaciones),
    }));
}

/**
 * @description Construye un objeto de solicitud para enviar un correo electrónico relacionado con un egreso, normalizando y formateando los datos de entrada según sea necesario.
 * @param input Datos de entrada para construir la solicitud de envío de correo de egreso, incluyendo destinatarios, asunto, mensaje principal, detalles del egreso y archivo adjunto.
 * @returns Objeto de solicitud listo para ser enviado a la API de mailing, con campos normalizados y formateados adecuadamente.
 */
export function buildSendEgresoEmailRequest(
    input: BuildEgresoEmailRequestInput,
): SendEgresoEmailRequest {
    const to = joinEmails(input.toEmails);
    const cc = joinEmails(input.ccEmails ?? []);
    const remoteId = normalizeOptional(input.selectedProofRemoteId);

    return {
        to,
        cc: cc || undefined,
        subject: String(input.subject ?? '').trim(),
        attachment: {
            name: normalizeOptional(input.selectedProofFileName) || 'Comprobante',
            remoteId: remoteId || undefined,
        },
        mainMessage: String(input.mainMessage ?? '').trim(),
        expenseList: mapEgresoDetailsToExpenseItems(input.egresoDetails ?? []),
    };
}

/**
 * @description Normaliza y une listas de correos electrónicos en un formato adecuado para envío.
 * @param emails Lista de correos electrónicos a normalizar y unir
 * @returns Cadena de correos electrónicos únicos, normalizados y separados por punto y coma
 */
function joinEmails(emails: string[]): string {
    const normalized = emails
        .map((email) => String(email ?? '').trim().toLowerCase())
        .filter((email) => Boolean(email));

    return [...new Set(normalized)].join(';');
}

/**
 * @description Normaliza un valor opcional, asegurando que se devuelva una cadena limpia y sin espacios innecesarios. Si el valor es nulo o indefinido, se devuelve una cadena vacía.
 * @param value Valor opcional a normalizar
 * @returns Cadena normalizada sin espacios innecesarios, o cadena vacía si el valor es nulo o indefinido
 */
function normalizeOptional(value?: string | null): string {
    return String(value ?? '').trim();
}
